import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { microsoftAPI, oauthAPI } from '../../services/api';

const TABS = {
  ALERTS: 'alerts',
  RISKY_USERS: 'risky-users',
  RISKY_SIGNINS: 'risky-signins',
};

const SEVERITY_COLORS = {
  critical: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  low: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  informational: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  unknown: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
};

const RISK_COLORS = {
  high: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  medium: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  low: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  none: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  hidden: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
  unknown: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
};

const STATUS_COLORS = {
  new: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  inProgress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  resolved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  dismissed: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
};

export default function SecurityAlertsPage() {
  const [activeTab, setActiveTab] = useState(TABS.ALERTS);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [premiumRequired, setPremiumRequired] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await oauthAPI.getStatus();
      const msConnected = response.data.microsoft?.connected || false;
      setMicrosoftConnected(msConnected);

      if (msConnected) {
        loadData();
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to check connection status');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (microsoftConnected) {
      loadData();
    }
  }, [activeTab, filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setPremiumRequired(false);

      const params = {};
      if (filter !== 'all') {
        if (activeTab === TABS.ALERTS) {
          params.severity = filter;
        } else {
          params.riskLevel = filter;
        }
      }

      let dataRes, summaryRes;

      switch (activeTab) {
        case TABS.ALERTS:
          [dataRes, summaryRes] = await Promise.all([
            microsoftAPI.getSecurityAlerts(params),
            microsoftAPI.getSecurityAlertsSummary(),
          ]);
          setData(dataRes.data.data.alerts);
          setSummary(summaryRes.data.data);
          break;

        case TABS.RISKY_USERS:
          [dataRes, summaryRes] = await Promise.all([
            microsoftAPI.getRiskyUsers(params),
            microsoftAPI.getRiskyUsersSummary(),
          ]);
          setData(dataRes.data.data.users);
          setSummary(summaryRes.data.data);
          if (summaryRes.data.data.requiresPremium) {
            setPremiumRequired(true);
          }
          break;

        case TABS.RISKY_SIGNINS:
          [dataRes, summaryRes] = await Promise.all([
            microsoftAPI.getRiskySignins(params),
            microsoftAPI.getRiskySigninsSummary(),
          ]);
          setData(dataRes.data.data.signins);
          setSummary(summaryRes.data.data);
          if (summaryRes.data.data.requiresPremium) {
            setPremiumRequired(true);
          }
          break;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load security data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      await microsoftAPI.triggerSecuritySync();
      await loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Sync failed';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  if (!microsoftConnected && !loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Security Alerts</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View security alerts, risky users, and risky sign-ins from Microsoft 365.
          </p>
        </div>

        <Card className="text-center py-12">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Microsoft 365 Not Connected
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Connect Microsoft 365 to view security alerts and risky users.
          </p>
          <Link to="/dashboard/connect">
            <Button>Connect Microsoft 365</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Security Alerts</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View security alerts, risky users, and risky sign-ins from Microsoft 365.
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-4">
          <button
            onClick={() => { setActiveTab(TABS.ALERTS); setFilter('all'); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TABS.ALERTS
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <ShieldExclamationIcon className="h-4 w-4 inline mr-2" />
            Security Alerts
          </button>
          <button
            onClick={() => { setActiveTab(TABS.RISKY_USERS); setFilter('all'); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TABS.RISKY_USERS
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <UserIcon className="h-4 w-4 inline mr-2" />
            Risky Users
          </button>
          <button
            onClick={() => { setActiveTab(TABS.RISKY_SIGNINS); setFilter('all'); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === TABS.RISKY_SIGNINS
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <GlobeAltIcon className="h-4 w-4 inline mr-2" />
            Risky Sign-ins
          </button>
        </nav>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Premium Required Banner */}
      {premiumRequired && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
          <div className="flex items-start gap-3">
            <LockClosedIcon className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-300">
                Azure AD Premium Required
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                This feature requires Azure AD Premium P1 or P2 license. Please upgrade your Microsoft 365 subscription to access risky users and risky sign-ins data.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && activeTab === TABS.ALERTS && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Critical</p>
              <p className="text-2xl font-bold text-red-600">{summary.bySeverity?.critical || 0}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">High</p>
              <p className="text-2xl font-bold text-orange-600">{summary.bySeverity?.high || 0}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Medium</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.bySeverity?.medium || 0}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Low</p>
              <p className="text-2xl font-bold text-green-600">{summary.bySeverity?.low || 0}</p>
            </div>
          </Card>
        </div>
      )}

      {summary && (activeTab === TABS.RISKY_USERS || activeTab === TABS.RISKY_SIGNINS) && !premiumRequired && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">High Risk</p>
              <p className="text-2xl font-bold text-red-600">{summary.byRiskLevel?.high || 0}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Medium Risk</p>
              <p className="text-2xl font-bold text-orange-600">{summary.byRiskLevel?.medium || 0}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Low Risk</p>
              <p className="text-2xl font-bold text-yellow-600">{summary.byRiskLevel?.low || 0}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-4">
          <FunnelIcon className="h-5 w-5 text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All</option>
            {activeTab === TABS.ALERTS ? (
              <>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </>
            ) : (
              <>
                <option value="high">High Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="low">Low Risk</option>
              </>
            )}
          </select>
        </div>
      </Card>

      {/* Last Sync Info */}
      {summary?.lastSyncedAt && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Last synced: {new Date(summary.lastSyncedAt).toLocaleString()}
        </p>
      )}

      {/* Data Table */}
      <Card padding={false}>
        {loading && !syncing ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="md" text="Loading alerts..." />
          </div>
        ) : premiumRequired ? (
          <div className="text-center py-12">
            <LockClosedIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              Azure AD Premium license required for this feature.
            </p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12">
            <ShieldExclamationIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {summary?.total === 0
                ? 'No data found. Click "Sync Now" to fetch data from Microsoft 365.'
                : 'No items match your filter criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {activeTab === TABS.ALERTS && (
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Alert
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {data.map((alert) => (
                    <tr key={alert.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {alert.title || 'Untitled Alert'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-md">
                            {alert.description}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.unknown}`}>
                          {alert.severity?.charAt(0).toUpperCase() + alert.severity?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[alert.status] || STATUS_COLORS.new}`}>
                          {alert.status || 'New'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {alert.source || alert.serviceSource || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {alert.createdAt ? new Date(alert.createdAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === TABS.RISKY_USERS && (
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Risk Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Risk State
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Detail
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {data.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {user.displayName || 'Unknown'}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {user.email || user.userPrincipalName}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${RISK_COLORS[user.riskLevel] || RISK_COLORS.unknown}`}>
                          {user.riskLevel?.charAt(0).toUpperCase() + user.riskLevel?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {user.riskState || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 max-w-xs truncate">
                        {user.riskDetail || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {user.riskLastUpdatedAt ? new Date(user.riskLastUpdatedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === TABS.RISKY_SIGNINS && (
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Risk Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Application
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {data.map((signin) => (
                    <tr key={signin.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {signin.displayName || 'Unknown'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {signin.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${RISK_COLORS[signin.riskLevel] || RISK_COLORS.unknown}`}>
                          {signin.riskLevel?.charAt(0).toUpperCase() + signin.riskLevel?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {signin.appDisplayName || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {signin.location || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {signin.ipAddress || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {signin.createdAt ? new Date(signin.createdAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
